import React from "react";
import styles from "./TicketCard.module.css";

/*
 * TicketCard — the notched-corner card (the reference's NEXT RACE card and the
 * product-grid cells): a 6px 45-degree cut on all four corners and a 1px rule.
 *
 *   <TicketCard tone="paper">…</TicketCard>
 *   <TicketCard as="li" tone="void">…</TicketCard>
 *   <TicketCard as={Link} to="/zeta" tone="olive">…</TicketCard>
 *
 * The notches are a clip-path, and clip-path clips real borders and outlines,
 * so the rule is drawn from two stacked pseudo-elements instead (a line-colour
 * layer, and a surface layer inset by the rule width with a matching polygon).
 * Focus and hover thicken that same rule, so the ring follows the notches and
 * can never be clipped away.
 *
 * A ticket is its own surface: it re-declares the contextual --section-*
 * properties for its tone, so a paper ticket on an olive panel has ink text
 * and an ink accent, and an olive ticket on paper has bone text.
 *
 * Props
 *   tone       "paper" | "olive" | "void"      surface (default "paper")
 *   as         tag name OR component           default "div". "li", "a",
 *              "article", "button", or e.g. react-router's Link — everything in
 *              ...rest (to, href, onClick, aria-*) is forwarded to it.
 *   className  extra class (size, grid placement, a custom padding)
 *   ...rest    forwarded to the element
 *
 * Tunable custom properties (from a className or style):
 *   --ticket-cut      corner cut, default 6px
 *   --ticket-rule     rule width, default 1px
 *   --ticket-line     rule colour at rest
 *   --ticket-pad      padding, default var(--space-5)
 *
 * No `transition` is set on the root, so a TicketCard can share its element
 * with <Reveal> (as={Reveal}, or Reveal as={TicketCard}) without replacing
 * Reveal's transition — the hover easing lives on the pseudo-element.
 */

const TONE_CLASS = {
  paper: styles.tonePaper,
  olive: styles.toneOlive,
  void: styles.toneVoid
};

function cx(...values) {
  return values.filter(Boolean).join(" ");
}

// forwardRef is load-bearing: <Reveal as={TicketCard}> hands its observer ref to
// this component. Without it the ref stays null, Reveal never observes the card,
// and the card is left at opacity 0 for good.
const TicketCard = React.forwardRef(function TicketCard(
  { tone = "paper", as = "div", className, children, ...rest },
  ref
) {
  const resolvedTone = TONE_CLASS[tone] ? tone : "paper";
  // Interactive = it navigates or acts. A component passed as `as` is judged by
  // its props (Link has `to`), so as={Reveal} does not pick up a pointer cursor.
  const isInteractive =
    as === "a" ||
    as === "button" ||
    Boolean(rest.href || rest.to || rest.onClick);

  return React.createElement(
    as,
    {
      "data-surface": resolvedTone,
      ...rest,
      ref,
      className: cx(
        styles.root,
        TONE_CLASS[resolvedTone],
        isInteractive && styles.interactive,
        className
      )
    },
    children
  );
});

export default TicketCard;
